export function teardownHyperbeamOnConversationOver({
  isConversationOver,
  hasActiveSession,
  cleanup,
}: {
  isConversationOver: boolean;
  hasActiveSession: boolean;
  cleanup: () => Promise<void>;
}): void {
  if (isConversationOver && hasActiveSession) {
    void cleanup();
  }
}

interface RenderingStateParams {
  isHyperbeamEnabled: boolean;
  hasActiveSession: boolean;
  isChecking: boolean;
  isHyperbeamLoading: boolean;
  canEmbed: boolean | null;
  error: string | null;
  contentUrl: string | null;
  isConversationOver?: boolean;
}

interface RenderingState {
  isLoading: boolean;
  showIframe: boolean;
  showHyperbeam: boolean;
  shouldFallbackToIframe: boolean;
}

/**
 * Determines if we should fallback to iframe when Hyperbeam session creation fails.
 */
function shouldFallbackToIframe(
  hasActiveSession: boolean,
  error: string | null,
  isHyperbeamLoading: boolean
): boolean {
  return !hasActiveSession && !!error && !isHyperbeamLoading;
}

/**
 * Determines if we should use Hyperbeam based on feature flag and embeddability.
 */
function shouldUseHyperbeam(
  isHyperbeamEnabled: boolean,
  hasActiveSession: boolean,
  canEmbed: boolean | null,
  shouldFallback: boolean
): boolean {
  if (!isHyperbeamEnabled) {
    return false;
  }

  // Use Hyperbeam if we have an active session
  if (hasActiveSession) {
    return true;
  }

  // Use Hyperbeam if URL is not embeddable (and not falling back)
  return canEmbed === false && !shouldFallback;
}

/**
 * Determines the loading state based on feature flag and current operations.
 */
function getLoadingState(
  isHyperbeamEnabled: boolean,
  hasActiveSession: boolean,
  isChecking: boolean,
  shouldUseHyperbeam: boolean,
  isHyperbeamLoading: boolean,
  shouldFallback: boolean
): boolean {
  // If Hyperbeam is disabled, no loading needed
  if (!isHyperbeamEnabled) {
    return false;
  }

  // Loading if checking embeddability (and no active session)
  if (!hasActiveSession && isChecking) {
    return true;
  }

  // Loading if creating Hyperbeam session (and no active session, not falling back)
  return (
    !hasActiveSession &&
    shouldUseHyperbeam &&
    isHyperbeamLoading &&
    !shouldFallback
  );
}

/**
 * Determines if we should show the iframe.
 */
function shouldShowIframe(
  isHyperbeamEnabled: boolean,
  isLoading: boolean,
  shouldUseHyperbeam: boolean,
  canEmbed: boolean | null,
  shouldFallback: boolean,
  contentUrl: string | null
): boolean {
  if (!contentUrl) {
    return false;
  }

  // If Hyperbeam is disabled, always show iframe
  if (!isHyperbeamEnabled) {
    return true;
  }

  // If loading or using Hyperbeam, don't show iframe
  if (isLoading || shouldUseHyperbeam) {
    return false;
  }

  // Show iframe if embeddable or falling back
  return canEmbed === true || shouldFallback;
}

/**
 * Determines if we should show Hyperbeam.
 */
function shouldShowHyperbeam(
  isHyperbeamEnabled: boolean,
  shouldUseHyperbeam: boolean,
  shouldFallback: boolean
): boolean {
  return isHyperbeamEnabled && shouldUseHyperbeam && !shouldFallback;
}

/**
 * Calculates all rendering state based on current conditions.
 * Centralizes all the complex conditional logic for easier testing and maintenance.
 */
export function calculateRenderingState(
  params: RenderingStateParams
): RenderingState {
  const {
    isHyperbeamEnabled,
    hasActiveSession,
    isChecking,
    isHyperbeamLoading,
    canEmbed,
    error,
    contentUrl,
    isConversationOver = false,
  } = params;

  // When conversation is over, show blank screen (like when UT starts)
  if (isConversationOver) {
    return {
      isLoading: false,
      showIframe: false,
      showHyperbeam: false,
      shouldFallbackToIframe: false,
    };
  }

  const shouldFallback = shouldFallbackToIframe(
    hasActiveSession,
    error,
    isHyperbeamLoading
  );

  const shouldUse = shouldUseHyperbeam(
    isHyperbeamEnabled,
    hasActiveSession,
    canEmbed,
    shouldFallback
  );

  const isLoading = getLoadingState(
    isHyperbeamEnabled,
    hasActiveSession,
    isChecking,
    shouldUse,
    isHyperbeamLoading,
    shouldFallback
  );

  const showIframe = shouldShowIframe(
    isHyperbeamEnabled,
    isLoading,
    shouldUse,
    canEmbed,
    shouldFallback,
    contentUrl
  );

  const showHyperbeam = shouldShowHyperbeam(
    isHyperbeamEnabled,
    shouldUse,
    shouldFallback
  );

  return {
    isLoading,
    showIframe,
    showHyperbeam,
    shouldFallbackToIframe: shouldFallback,
  };
}
